import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Container } from 'reactstrap';
import {
  FiArrowRight,
  FiBriefcase,
  FiCheck,
  FiChevronDown,
  FiCode,
  FiEye,
  FiGrid,
  FiLayers,
  FiMessageSquare,
  FiMonitor,
  FiPlay,
  FiSliders,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import './GenAI.css';

const sectionLinks = [
  { href: '#genai-features', label: 'Features' },
  { href: '#genai-use-cases', label: 'Use Cases' },
  { href: '#genai-pricing', label: 'Plans' },
  { href: '#genai-faq', label: 'FAQ' },
];

const valueStrip = [
  '100 AI Development Credits included',
  'App workspace included',
  'Managed Cloud and Dedicated VM options',
  'Clear plans for each project stage',
];

const promiseStats = [
  { value: '100 AI', label: 'Development Credits included in every plan' },
  { value: 'Workspace', label: 'test, review, and improve in one place' },
  { value: 'Separate environment', label: 'for building, previewing, and publishing' },
  { value: 'Managed runtime', label: 'built your app without managing servers, SSL, or deployment' },
];

const optionCards = [
  {
    title: 'GenAI Cloud',
    badge: 'Start fast',
    description: 'Managed Cloud runtime',
    points: [
      'Best for landing pages, simple websites, lightweight apps, and early MVPs',
      'Lower monthly cost',
      'No dedicated VM',
    ],
  },
  {
    title: 'GenAI Dedicated',
    badge: 'Dedicated VM',
    description: 'Defined CPU, RAM, SSD, and bandwidth',
    points: [
      'Best for business applications and heavier workloads',
      'More predictable resources',
      'More control',
    ],
  },
];

const steps = [
  {
    title: 'Describe what you want to build',
    description: 'Start with the idea, workflow, pages, or features you want to create.',
    icon: FiMessageSquare,
  },
  {
    title: 'Build and improve with AI',
    description: 'Use AI to create, refine, fix, and move faster toward a working version.',
    icon: FiCode,
  },
  {
    title: 'Test in workspace',
    description: 'Review changes, compare versions, and improve the app in a practical workspace.',
    icon: FiEye,
  },
  {
    title: 'Run in the environment that fits your project',
    description:
      'Start on GenAI Cloud for a managed and simple runtime. If your application needs stronger resources, a dedicated VM, or more isolation, you can move to GenAI Dedicated.',
    icon: FiPlay,
  },
];

const benefits = [
  {
    title: 'Build faster with AI',
    description: 'Move from idea to usable product faster with AI helping you create, improve, and fix.',
    icon: FiZap,
  },
  {
    title: 'A workspace for real progress',
    description: 'Test versions, review changes, and keep moving without scattered tools.',
    icon: FiMonitor,
  },
  {
    title: 'Start simple, scale when needed',
    description:
      'Begin with GenAI Cloud for a fast and managed setup. When your project needs more power, stronger isolation, or dedicated resources, upgrade to GenAI Dedicated.',
    icon: FiTrendingUp,
  },
  {
    title: 'No need to start from infrastructure',
    description: 'Skip early server setup and focus on building before infrastructure becomes the main task.',
    icon: FiLayers,
  },
  {
    title: 'Built for ideas and real projects',
    description: 'Use it for MVPs, internal tools, client work, business apps, and product experiments.',
    icon: FiGrid,
  },
  {
    title: 'Clear and flexible pricing',
    description: 'Every plan includes 100 AI Development Credits, and more can be added when needed.',
    icon: FiSliders,
  },
];

const oldWay = [
  'More setup before real progress begins',
  'Testing flow built from scratch',
  'Extra friction before client presentation',
  'Deployment questions too early',
  'Infrastructure work before product learning',
  'Slower movement from idea to feedback',
];

const genaiWay = [
  'Start with the product idea',
  'Build and improve with AI',
  'Test in workspace',
  'Run in the right environment',
  'Upgrade when the project needs more',
  'Show results faster',
];

const audiences = [
  {
    title: 'Entrepreneurs',
    description: 'Validate an idea, launch an MVP, and reach a working version faster.',
    icon: FiTrendingUp,
  },
  {
    title: 'Small Businesses',
    description: 'Create internal tools, dashboards, and business applications with a simpler setup path.',
    icon: FiBriefcase,
  },
  {
    title: 'Agencies',
    description: 'Build faster for clients, present working versions earlier, and scale serious projects later.',
    icon: FiUsers,
  },
  {
    title: 'Developers',
    description: 'Reduce setup time and move faster through early product cycles.',
    icon: FiCode,
  },
  {
    title: 'Product Builders',
    description: 'Turn rough concepts into usable software and keep improving as the project grows.',
    icon: FiSliders,
  },
];

const useCaseGroups = [
  {
    title: 'GenAI Cloud',
    subtitle: 'Best for fast starts, simple projects, and early-stage ideas.',
    description:
      'Use GenAI Cloud to validate ideas, build simple applications, test client concepts, and launch lightweight projects without managing infrastructure.',
    items: [
      'Landing pages',
      'Business websites',
      'Product prototypes',
      'Early MVPs',
      'Campaign pages',
      'Service pages',
      'Simple dashboards',
      'Client previews',
      'Basic internal tools',
      'Idea validation projects',
    ],
    tags: ['Fast start', 'Managed cloud', 'Lower cost', 'Simple launch'],
  },
  {
    title: 'GenAI Dedicated',
    subtitle: 'Best for serious projects, business applications, and dedicated resources.',
    description:
      'Move to GenAI Dedicated when your application becomes more important, needs more power, stronger isolation, or a dedicated VM for production use.',
    items: [
      'Customer portals',
      'Admin dashboards',
      'Order management systems',
      'Lead tracking systems',
      'Quote and proposal tools',
      'Booking systems',
      'Reporting dashboards',
      'Operations Workspaces',
      'Early SaaS applications',
      'Client production projects',
      'Apps with heavier usage',
      'Projects that need dedicated resources',
    ],
    tags: ['Dedicated VM', 'More isolation', 'More resources', 'Production-oriented'],
  },
];

const cloudStarterPlan = {
  name: 'GenAI Cloud Starter',
  tag: 'Recommended start',
  price: '$25',
  description:
    'Best for landing pages, simple websites, early MVPs, and lightweight applications that need a fast managed environment.',
  cta: 'Start with Cloud',
  href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-cloud',
  specs: [
    '100 AI Development Credits',
    'App workspace',
    'Managed Cloud runtime',
    'Separate Builder, Preview, and Production environments',
    'Temporary domain',
    'Custom domain connection',
    'SSL',
    'External database, storage, and API connection supported',
    'Fair usage applies',
  ],
};

const privatePlans = [
  {
    name: 'GenAI Dedicated Core',
    tag: '',
    price: '$59',
    description: 'For an initial MVP, lightweight application, or small business project that needs a dedicated VM.',
    cta: 'Start with Dedicated Core',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-core',
    specs: [
      'Dedicated VM Environment',
      '2 vCPU',
      '4 GB RAM',
      '40 GB SSD Storage',
      '20 TB Monthly Bandwidth',
      '100 AI Development Credits',
      'App workspace',
    ],
  },
  {
    name: 'GenAI Dedicated Edge',
    tag: 'Most popular',
    price: '$119',
    description: 'For growing projects, business applications, client work, and environments that need more power.',
    cta: 'Choose Dedicated Edge',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-software-builder-genai-edge',
    featured: true,
    specs: [
      'Dedicated VM Environment',
      '4 vCPU',
      '8 GB RAM',
      '160 GB SSD Storage',
      '20 TB Monthly Bandwidth',
      '100 AI Development Credits',
      'App workspace',
    ],
  },
  {
    name: 'GenAI Dedicated Supreme',
    tag: 'For higher performance',
    price: '$249',
    description: 'For more complex applications, heavier workloads, and projects that need stronger resources.',
    cta: 'Upgrade to Dedicated Supreme',
    href: 'https://my.epicfinal.com/index.php?rp=/store/managed-genai-software-builder/genai-supreme',
    specs: [
      'Dedicated VM Environment',
      '8 vCPU',
      '16 GB RAM',
      '320 GB SSD Storage',
      '20 TB Monthly Bandwidth',
      '100 AI Development Credits',
      'App workspace',
    ],
  },
];

const upgrades = [
  { amount: '500 AI Development Credits', price: 'Add $59 per month' },
  { amount: '2,000 AI Development Credits', price: 'Add $219 per month' },
];

const faqItems = [
  {
    question: 'What is GenAI?',
    answer:
      'GenAI is an AI-powered environment for building, improving, testing, and running applications. It combines AI-assisted development, app workspace, and managed runtime options.',
  },
  {
    question: 'What is the difference between GenAI Cloud and GenAI Dedicated?',
    answer:
      'GenAI Cloud is a managed Cloud environment for starting quickly. It is best for landing pages, simple websites, lightweight applications, and early MVPs. GenAI Dedicated includes a dedicated VM with defined CPU, RAM, SSD storage, and bandwidth, and is better suited for projects that need stronger isolation, predictable resources, or heavier workloads.',
  },
  {
    question: 'Does GenAI Cloud include a dedicated VM?',
    answer:
      'No. GenAI Cloud does not include a dedicated VM. It runs on managed Cloud infrastructure with separate environments per customer. If you need a dedicated VM, choose GenAI Dedicated.',
  },
  {
    question: 'Can I connect an external database or storage service?',
    answer:
      'Yes. GenAI Cloud can connect to external databases, storage services, and APIs.',
  },
  {
    question: 'Does GenAI Cloud include persistent storage?',
    answer:
      'GenAI Cloud does not include an internal persistent database or local persistent file storage by default. For data, uploads, or files that must remain permanently available, use an external database/storage service or choose GenAI Dedicated when internal storage is required.',
  },
  {
    question: 'When should I choose GenAI Dedicated?',
    answer:
      'Choose GenAI Dedicated when your project needs a dedicated VM, stronger isolation, predictable resources, heavier workloads, internal storage, or more advanced control.',
  },
  {
    question: 'Can I upgrade later?',
    answer:
      'Yes. You can start with GenAI Cloud and move to GenAI Dedicated when your project needs more resources or a dedicated VM.',
  },
  {
    question: 'What are AI Development Credits?',
    answer:
      'AI Development Credits are used when you ask AI to build, improve, fix, or update your application.',
  },
  {
    question: 'What happens if I run out of AI Development Credits?',
    answer:
      'You can add a monthly AI Development Credits package based on your usage.',
  },
];

const sectionMotion = {
  initial: { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, ease: 'easeOut' },
};

export default function GenAI() {
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="genai-page genai-page--en">
      <Helmet>
        <title>GenAI - AI Builder with Cloud or Dedicated Environment | EpicFinal</title>
        <meta
          name="description"
          content="Build apps with AI, test them in app workspaces, and run them on GenAI Cloud or GenAI Dedicated. Start simple and upgrade to dedicated resources when your project needs them."
        />
        <link rel="canonical" href="https://epicfinal.com/genai" />
      </Helmet>

      <section className="genai-hero">
        <Container>
          <motion.div className="genai-hero-shell" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }}>
            <div className="genai-hero-grid">
              <div className="genai-hero-copy">
                <div className="genai-eyebrow">GenAI Software Builder</div>
                <h1 className="genai-hero-title genai-hero-title--wide">
                  <span className="genai-hero-keyword genai-hero-keyword--build">Build</span> apps with AI.{' '}
                  <span className="genai-hero-keyword genai-hero-keyword--test">Test</span> in Workspace.{' '}
                  <span className="genai-hero-keyword genai-hero-keyword--deploy">Deploy</span> your way.
                </h1>
                <p className="genai-hero-text">
                  Bring your idea to life faster with AI. Test every version in your workspace, improve it, and publish to production with one click.
                </p>
                <p className="genai-hero-text genai-hero-text-secondary">
                  Start on GenAI Cloud, then upgrade to GenAI Dedicated when your project needs dedicated resources.
                </p>

                <div className="genai-hero-actions">
                  <a
                    href={cloudStarterPlan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="genai-btn genai-btn-primary"
                  >
                    Get Started
                    <FiArrowRight aria-hidden="true" />
                  </a>
                  <a href="#genai-pricing" className="genai-btn genai-btn-secondary">
                    View Plans
                  </a>
                </div>

                <p className="genai-hero-note">
                  Ideal for landing pages, MVPs, business tools, internal systems, client projects, and fast development environments.
                </p>
              </div>

              <div className="genai-hero-visual" aria-hidden="true">
                <div className="genai-workspace">
                  <div className="genai-workspace-top">
                    <div className="genai-window-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="genai-visual-badge">Cloud or Dedicated Environment</div>
                  </div>

                  <div className="genai-prompt-box">
                    <span className="genai-prompt-label">Prompt</span>
                    <p>Build me a customer portal with login, a dashboard, an orders table, and an admin area.</p>
                  </div>

                  <div className="genai-visual-steps">
                    {['Describe', 'Build with AI', 'Test in workspace', 'Publish & Run'].map((item, index) => (
                      <div key={item} className="genai-visual-step">
                        <span className="genai-visual-step-index">0{index + 1}</span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>

                  <div className="genai-visual-caption">
                    Separate environments for building, previewing, and publishing.
                  </div>
                </div>
              </div>
            </div>

            <div className="genai-value-strip">
              {valueStrip.map((item) => (
                <div key={item} className="genai-value-item">
                  <FiCheck aria-hidden="true" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-subnav">
        <Container>
          <div className="genai-subnav-track">
            {sectionLinks.map((item) => (
              <a key={item.href} href={item.href} className="genai-subnav-link">
                {item.label}
              </a>
            ))}
          </div>
        </Container>
      </section>

      <section id="genai-features" className="genai-section">
        <Container>
          <motion.div className="genai-promise-grid" {...sectionMotion}>
            <div className="genai-section-copy">
              <h2 className="genai-section-title genai-section-title--wide">From your first idea to a working application</h2>
              <p className="genai-section-text">
                GenAI helps you move from idea to working application without making infrastructure the first decision.
              </p>
              <p className="genai-section-text">
                Describe what you want to build, improve it with AI, test changes in workspace, and run the project in the environment that fits your stage.
              </p>
            </div>

            <div className="genai-stat-grid">
              {promiseStats.map((item) => (
                <div key={item.label} className="genai-stat-card">
                  <strong>{item.value}</strong>
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-how-it-works" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">How It Works</div>
              <h2 className="genai-section-title">How GenAI Works</h2>
            </div>

            <div className="genai-steps-grid">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="genai-step-card">
                    <div className="genai-step-topline">
                      <span className="genai-step-number">Step {index + 1}</span>
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Why GenAI</div>
              <h2 className="genai-section-title">Why Choose GenAI?</h2>
            </div>

            <div className="genai-benefits-grid">
              {benefits.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="genai-benefit-card">
                    <div className="genai-benefit-icon">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-section genai-compare-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Old Way vs GenAI</div>
              <h2 className="genai-section-title">Less setup. Faster testing. Less infrastructure friction.</h2>
            </div>

            <div className="genai-compare-grid">
              <div className="genai-compare-panel">
                <h3>The old way</h3>
                <ul>
                  {oldWay.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div className="genai-compare-panel genai-compare-panel-accent">
                <h3>With GenAI</h3>
                <ul>
                  {genaiWay.map((item) => (
                    <li key={item}>
                      <FiCheck aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-audience" className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Who It Is For</div>
              <h2 className="genai-section-title">Who GenAI Is For</h2>
            </div>

            <div className="genai-audience-grid">
              {audiences.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="genai-audience-card">
                    <div className="genai-audience-icon">
                      <Icon aria-hidden="true" />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-use-cases" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Use Cases</div>
              <h2 className="genai-section-title genai-section-title--wide">Choose the right environment for your project stage</h2>
              <p className="genai-section-subtitle">
                Start simple with GenAI Cloud, then move to GenAI Dedicated when your project needs stronger resources, more isolation, or a dedicated VM.
              </p>
            </div>

            <div className="genai-usecase-groups">
              {useCaseGroups.map((group) => (
                <div
                  key={group.title}
                  className={`genai-usecase-group-card ${
                    group.title === 'GenAI Cloud'
                      ? 'genai-usecase-group-card--cloud'
                      : 'genai-usecase-group-card--dedicated'
                  }`}
                >
                  <h3>{group.title}</h3>
                  <p className="genai-usecase-group-subtitle">{group.subtitle}</p>
                  <p className="genai-usecase-group-description">{group.description}</p>
                  <div className="genai-usecase-cloud">
                    {group.items.map((item) => (
                      <span key={item} className="genai-usecase-pill">
                        {item}
                      </span>
                    ))}
                  </div>
                  <div className="genai-usecase-tags">
                    {group.tags.map((tag) => (
                      <span key={tag} className="genai-usecase-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <p className="genai-option-summary">Start with Cloud. Move to Dedicated when your project becomes more serious.</p>
          </motion.div>
        </Container>
      </section>

      {/* <section className="genai-section genai-two-options-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Two Runtime Options</div>
              <h2 className="genai-section-title genai-section-title--wide">Start fast with GenAI Cloud. Upgrade when the project needs more.</h2>
            </div>

            <div className="genai-two-options-grid">
              {optionCards.map((item) => (
                <div
                  key={item.title}
                  className={`genai-option-card${item.title === 'GenAI Cloud' ? ' genai-option-card--cloud' : ' genai-option-card--private'}`}
                >
                  <span className="genai-option-badge">{item.badge}</span>
                  <h3>{item.title}</h3>
                  <p className="genai-option-description">{item.description}</p>
                  <ul className="genai-option-list">
                    {item.points.map((point) => (
                      <li key={point}>
                        <FiCheck aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <p className="genai-option-summary">
              Most projects can start with GenAI Cloud. When your application needs dedicated resources, stronger isolation, or more predictable performance, GenAI Dedicated gives you a dedicated VM environment.
            </p>
          </motion.div>
        </Container>
      </section> */}

      <section id="genai-pricing" className="genai-section">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">Pricing</div>
              <h2 className="genai-section-title genai-section-title--wide">Choose the GenAI environment that fits your stage</h2>
              <p className="genai-section-subtitle">
                Start with GenAI Cloud for a simple managed environment. Choose GenAI Dedicated when your project needs a dedicated VM, stronger isolation, and defined server resources.
              </p>
            </div>

            <div className="genai-cloud-plan-card">
              <div className="genai-cloud-plan-shell">
                <div className="genai-plan-top">
                  <span className="genai-plan-tag">{cloudStarterPlan.tag}</span>
                  <h3>{cloudStarterPlan.name}</h3>
                  <div className="genai-plan-price">
                    <strong>{cloudStarterPlan.price}</strong>
                    <span>per month</span>
                  </div>
                  <p>{cloudStarterPlan.description}</p>
                </div>

                <ul className="genai-plan-list">
                  {cloudStarterPlan.specs.map((item) => (
                    <li key={item}>
                      <FiCheck aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={cloudStarterPlan.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="genai-btn genai-btn-primary"
                >
                  {cloudStarterPlan.cta}
                </a>
              </div>
            </div>

            <div className="genai-private-plans-heading">
              <div className="genai-section-kicker">GenAI Dedicated - Dedicated VM Plans</div>
              <h3>For projects that need stronger resources, more isolation, and a dedicated VM environment.</h3>
            </div>

            <div className="genai-pricing-grid">
              {privatePlans.map((plan) => (
                <div key={plan.name} className={`genai-plan-card${plan.featured ? ' is-featured' : ''}`}>
                  <div className="genai-plan-top">
                    {plan.tag ? <span className="genai-plan-tag">{plan.tag}</span> : null}
                    <h3>{plan.name}</h3>
                    <div className="genai-plan-price">
                      <strong>{plan.price}</strong>
                      <span>per month</span>
                    </div>
                    <p>{plan.description}</p>
                  </div>

                  <ul className="genai-plan-list">
                    {plan.specs.map((item) => (
                      <li key={item}>
                        <FiCheck aria-hidden="true" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <a
                    href={plan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`genai-btn ${plan.featured ? 'genai-btn-primary' : 'genai-btn-secondary'}`}
                  >
                    {plan.cta}
                  </a>
                </div>
              ))}
            </div>

            <div className="genai-upgrades-block">
              <div className="genai-upgrades-copy">
                <div className="genai-section-kicker">AI Development Credit Upgrades</div>
                <h3>Need more AI Development Credits?</h3>
                <p>
                  Every plan includes 100 AI Development Credits. If you are building more, improving more, or working on more active projects, you can add a monthly credit package.
                </p>
                <small>AI Development Credits are used to build, improve, fix, and update your application with AI.</small>
              </div>

              <div className="genai-upgrades-list">
                {upgrades.map((item) => (
                  <div key={item.amount} className="genai-upgrade-card">
                    <strong>{item.amount}</strong>
                    <span>{item.price}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="genai-clarity-panel">
              <div>
                <div className="genai-section-kicker">Trust / Clarity</div>
                <h3>Transparent infrastructure. Flexible usage.</h3>
                <p>
                  GenAI is designed to make building and running applications simpler, while keeping the important details clear: which environment you receive, what is included, and how AI Development Credits work.
                </p>
              </div>

              <ul className="genai-check-list">
                <li><FiCheck aria-hidden="true" /><span>Start with managed GenAI Cloud</span></li>
                <li><FiCheck aria-hidden="true" /><span>Upgrade to a dedicated VM when needed</span></li>
                <li><FiCheck aria-hidden="true" /><span>Dedicated VM available in GenAI Dedicated plans</span></li>
                <li><FiCheck aria-hidden="true" /><span>Resources are clearly presented</span></li>
                <li><FiCheck aria-hidden="true" /><span>Add AI Development Credits when needed</span></li>
                <li><FiCheck aria-hidden="true" /><span>Choose the environment based on your project stage</span></li>
              </ul>
            </div>
          </motion.div>
        </Container>
      </section>

      <section id="genai-faq" className="genai-section genai-section-alt">
        <Container>
          <motion.div {...sectionMotion}>
            <div className="genai-section-heading">
              <div className="genai-section-kicker">FAQ</div>
              <h2 className="genai-section-title">Frequently Asked Questions</h2>
            </div>

            <div className="genai-faq-list">
              {faqItems.map((item, index) => {
                const isOpen = openFaqIndex === index;
                return (
                  <div key={item.question} className={`genai-faq-item${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      className="genai-faq-question"
                      onClick={() => setOpenFaqIndex(isOpen ? -1 : index)}
                      aria-expanded={isOpen}
                      aria-controls={`genai-faq-panel-${index}`}
                    >
                      <span>{item.question}</span>
                      <FiChevronDown aria-hidden="true" />
                    </button>
                    <div id={`genai-faq-panel-${index}`} className="genai-faq-answer">
                      <p>{item.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </Container>
      </section>

      <section className="genai-cta-section">
        <Container>
          <motion.div className="genai-cta-shell" {...sectionMotion}>
            <div>
              <div className="genai-section-kicker">Final CTA</div>
              <h2>Have an idea for an app? Now you need an environment that helps turn it into a working product.</h2>
              <p>
                GenAI combines AI-powered building, app workspace, and flexible runtime options so you can move faster from idea to a version you can test and run.
              </p>
              <p>
                Start with GenAI Cloud for a simple managed setup, or choose GenAI Dedicated when your project needs a dedicated VM.
              </p>
            </div>

            <div className="genai-cta-actions">
              <a href="#genai-pricing" className="genai-btn genai-btn-primary">
                Choose a Plan
              </a>
              <a
                href="https://meetings.hubspot.com/viacheslav-seifer"
                target="_blank"
                rel="noopener noreferrer"
                className="genai-btn genai-btn-secondary"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}
